import type {
  AccessSettings,
  AssignableRole,
  DeactivationPreview,
  OffboardingRecord,
  PendingGrant,
  PeopleCounts,
  PersonDetail,
  PersonPage,
  Role,
  SignInFailure
} from '@ralph/contracts'
import { api, apiGet, apiSend } from '@/api/client.ts'

/**
 * The people plane's reads and writes (PRD-21).
 *
 * Every one of these is the same first-party API the integration suite drives directly; nothing
 * on this surface is UI-only. The refusals that matter — the last administrator, self-demotion,
 * the typed-name gate, four-eyes — are decided by the server and arrive here as codes, which is
 * why the components branch on `ApiRequestError.code` rather than on any local guess.
 */

export type PeopleFilters = {
  q?: string
  role?: Role | ''
  status?: 'active' | 'deactivated' | ''
  group?: string
}

const peopleQuery = (filters: PeopleFilters, cursor?: string | null): string => {
  const params = new URLSearchParams()
  if (filters.q?.trim()) params.set('q', filters.q.trim())
  if (filters.role) params.set('role', filters.role)
  if (filters.status) params.set('status', filters.status)
  if (filters.group) params.set('group', filters.group)
  if (cursor) params.set('cursor', cursor)
  const query = params.toString()
  return query === '' ? '' : `?${query}`
}

export const fetchPeople = (filters: PeopleFilters, cursor?: string | null): Promise<PersonPage> =>
  apiGet<PersonPage>(`/api/admin/users${peopleQuery(filters, cursor)}`)

export const fetchPeopleCounts = (): Promise<PeopleCounts> => apiGet<PeopleCounts>('/api/admin/users/counts')

export const fetchPerson = (id: string): Promise<PersonDetail> => apiGet<PersonDetail>(`/api/admin/users/${id}`)

export const peopleExportUrl = (filters: PeopleFilters): string => `/api/admin/users/export${peopleQuery(filters)}`

export type GrantInput = {
  role: AssignableRole
  effect?: 'allow' | 'deny'
  reason?: string
  expiresAt?: string | null
}

export const grantRole = (userId: string, input: GrantInput) =>
  apiSend<{ grantId: string; roles: Role[]; pendingApproval: boolean }>(
    `/api/admin/users/${userId}/roles`,
    'POST',
    input
  )

export const revokeRole = (userId: string, role: Role, effect: 'allow' | 'deny', reason: string) =>
  apiSend<{ grantId: string; roles: Role[] }>(`/api/admin/users/${userId}/roles/${role}?effect=${effect}`, 'DELETE', {
    reason
  })

export const approveGrant = (userId: string, grantId: string) =>
  apiSend<{ roles: Role[] }>(`/api/admin/users/${userId}/roles/${grantId}/approve`, 'POST', {})

export const fetchDeactivationPreview = (userId: string): Promise<DeactivationPreview> =>
  apiGet<DeactivationPreview>(`/api/admin/users/${userId}/deactivate/preview`)

export const deactivatePerson = (userId: string, input: { reason: string; confirmName?: string }) =>
  apiSend<OffboardingRecord>(`/api/admin/users/${userId}/deactivate`, 'POST', input)

export const reactivatePerson = (userId: string, reason: string) =>
  apiSend<{ roles: Role[] }>(`/api/admin/users/${userId}/reactivate`, 'POST', { reason })

export const revokeSessions = (userId: string) =>
  apiSend<{ revoked: number }>(`/api/admin/users/${userId}/sessions/revoke`, 'POST', {})

export const fetchPendingGrants = (): Promise<{ items: PendingGrant[] }> =>
  apiGet<{ items: PendingGrant[] }>('/api/admin/access/pending')

export const preparePendingGrant = (input: {
  email: string
  role: AssignableRole
  reason?: string
  expiresAt?: string | null
}) =>
  apiSend<{ id: string; applied: boolean; userId: string | null; pendingApproval: boolean }>(
    '/api/admin/access/pending',
    'POST',
    input
  )

export const withdrawPendingGrant = (id: string) =>
  apiSend<{ email: string; role: string }>(`/api/admin/access/pending/${id}`, 'DELETE', {})

export const fetchSignInFailures = (): Promise<{ items: SignInFailure[] }> =>
  apiGet<{ items: SignInFailure[] }>('/api/admin/access/sign-in-failures')

export const fetchAccessSettings = (): Promise<AccessSettings> => apiGet<AccessSettings>('/api/admin/access/settings')

export const saveAccessSettings = (settings: AccessSettings) =>
  api<AccessSettings>('/api/admin/access/settings', { method: 'PUT', body: JSON.stringify(settings) })
